import styled from 'styled-components';

export const ReservationsContainer = styled.div`
  background: var(--bg-section);
  min-height: calc(100vh - 68px);
  padding: 40px 20px 72px;

  > * {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }
`;

export const Header = styled.header`
  margin-bottom: 24px;

  h1 {
    font-size: var(--text-3xl);
    letter-spacing: -0.5px;
    margin-bottom: 4px;
  }

  p {
    color: var(--text-secondary);
  }
`;

export const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 24px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const StatCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 18px 20px;
  box-shadow: var(--shadow-xs);
  border-top: 3px solid ${props => props.color || 'var(--primary)'};

  .stat-number {
    font-size: var(--text-2xl);
    font-weight: 700;
    color: var(--text-primary);
    line-height: 1.1;
  }

  .stat-label {
    font-size: var(--text-xs);
    color: var(--text-secondary);
    margin-top: 2px;
  }
`;

export const FiltersSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 16px 18px;
  margin-bottom: 28px;

  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

export const FilterGroup = styled.div`
  label {
    display: block;
    font-size: var(--text-xs);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 6px;
  }

  input, select {
    width: 100%;
    padding: 9px 12px;
    background: var(--bg-input);
    border: 1.5px solid transparent;
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-family: var(--font-brand);
    font-size: var(--text-sm);

    &:focus {
      outline: none;
      border-color: var(--border-focus);
    }
  }

  select { cursor: pointer; }
`;

export const ReservationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ReservationCard = styled.article`
  background: ${props => (props.status === 'cancelled') ? 'var(--bg-section)' : 'var(--bg-card)'};
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 22px 24px;
  box-shadow: var(--shadow-xs);

  .reservation-notes,
  .scan-info,
  .cancel-info {
    margin-top: 14px;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    font-size: var(--text-sm);
  }

  .reservation-notes {
    background: var(--bg-section);
    color: var(--text-secondary);
  }

  .scan-info {
    background: var(--success-subtle);
    color: var(--success-dark);
  }

  .cancel-info {
    background: var(--danger-subtle);
    color: var(--danger-dark);
  }
`;

export const ReservationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-light);

  h3 {
    font-family: monospace;
    font-size: var(--text-base);
    margin-bottom: 6px;
    color: var(--text-secondary);
  }

  .reservation-date {
    font-size: var(--text-xs);
    color: var(--text-muted);
    white-space: nowrap;
  }
`;

export const ReservationDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;

  @media (max-width: 1000px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }

  h4 {
    font-size: var(--text-xs);
    font-family: var(--font-brand);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 8px;
  }

  p {
    font-size: var(--text-sm);
    color: var(--text-secondary);
    margin: 3px 0;
    word-break: break-word;
  }
`;

export const CustomerInfo = styled.div``;
export const ActivityInfo = styled.div``;

export const PaymentInfo = styled.div`
  .payment-status {
    font-weight: 700;
    margin-top: 6px;

    &.held { color: var(--primary); }
    &.released { color: var(--success-dark); }
    &.refunded { color: var(--text-muted); }
    &.pending_payment { color: var(--warning-dark); }
  }
`;

export const QRCode = styled.div`
  .qr-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 10px;

    button {
      background: var(--bg-input);
      border: 1.5px solid var(--border-medium);
      color: var(--text-primary);
      padding: 6px 12px;
      border-radius: var(--radius-round);
      font-family: var(--font-brand);
      font-size: var(--text-xs);
      font-weight: 600;
      cursor: pointer;
      transition: all var(--transition-fast);

      &:hover {
        border-color: var(--primary);
        color: var(--primary);
      }
    }
  }
`;

export const ActionButtons = styled.div`
  margin-top: 8px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 64px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  h3 { margin-bottom: 8px; }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 28px;

  span {
    font-size: var(--text-sm);
    color: var(--text-secondary);
  }

  button {
    background: var(--bg-card);
    border: 1.5px solid var(--border-medium);
    color: var(--text-primary);
    padding: 8px 18px;
    border-radius: var(--radius-round);
    font-family: var(--font-brand);
    font-size: var(--text-sm);
    font-weight: 600;
    cursor: pointer;
    transition: all var(--transition-fast);

    &:hover:not(:disabled) {
      border-color: var(--primary);
      color: var(--primary);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;
